// Options > MON AGENDA : coller l'adresse iCal secrète de son agenda.
//
// Chacun branche le SIEN (il le suit d'un projet à l'autre). Zygosight n'y lit
// que des horaires occupés, pour proposer vos créneaux libres quand vous
// invitez, et retirer un créneau qui se remplit entre-temps.
import { useState, type FormEvent } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, Chip, ErrorBanner, Eyebrow, Field, Spinner } from '../components/ui';
import { emailFromCalendarUrl, useMyCalendar, useRemoveCalendar, useSaveCalendar, useSetCalendarEmail } from './api';

const errorOf = (cause: unknown) =>
  cause instanceof ApiRequestError ? cause.message : 'Une erreur inattendue est survenue.';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export function CalendarSection() {
  const query = useMyCalendar();
  const save = useSaveCalendar();
  const remove = useRemoveCalendar();
  const setMail = useSetCalendarEmail();
  const [url, setUrl] = useState('');
  // L'e-mail : lu dans l'adresse de Google tant qu'on ne l'a pas tapé soi-même.
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingMail, setEditingMail] = useState(false);
  const [mail, setMailValue] = useState('');
  const cal = query.data;

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await save.mutateAsync({ url: url.trim(), email: email.trim() });
      setUrl('');
      setEmail('');
      setEmailTouched(false);
      setEditing(false);
    } catch {
      /* l'erreur est affichée sous le champ */
    }
  }

  return (
    <section className="zy-card opt-section" aria-labelledby="cal-title">
      <div className="opt-head">
        <div>
          <Eyebrow>Mon agenda</Eyebrow>
          <h2 className="t-h3" id="cal-title">Proposer mes créneaux libres</h2>
          <p className="card-lede">
            Branchez votre agenda : quand vous invitez des participants, Zygosight leur propose vos créneaux
            libres, et retire un créneau dès qu’il se remplit dans votre agenda. Chacun branche le sien.
          </p>
        </div>
      </div>

      {query.isPending ? (
        <Spinner label="Chargement de votre agenda" />
      ) : query.isError ? (
        <ErrorBanner>{errorOf(query.error)}</ErrorBanner>
      ) : (
        <>
          {cal?.configured && !editing ? (
            <div className="cal-state">
              <p>
                <Chip state={cal.error ? 'avertissement' : 'succes'}>
                  {cal.error ? 'À vérifier' : 'Branché'}
                </Chip>{' '}
                <strong>{cal.host}</strong> · dernière lecture {when(cal.checkedAt)}
              </p>
              {cal.error ? <p className="zy-texte-muet">{cal.error}</p> : null}
              {editingMail ? (
                <form
                  className="cal-mail"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setMail.mutate(mail.trim(), { onSuccess: () => setEditingMail(false) });
                  }}
                >
                  <Field label="E-mail de l’agenda" type="email" value={mail} onChange={(e) => setMailValue(e.target.value)} autoComplete="email" />
                  <Button type="submit" variant="primary" loading={setMail.isPending}>Enregistrer</Button>
                  <Button type="button" variant="ghost" onClick={() => setEditingMail(false)}>Annuler</Button>
                </form>
              ) : (
                <p>
                  E-mail : <strong>{cal.email ?? cal.accountEmail ?? '—'}</strong>
                  {!cal.email && cal.accountEmail ? ' (celui de votre compte)' : ''}{' '}
                  <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => { setMailValue(cal.email ?? cal.accountEmail ?? ''); setEditingMail(true); }}>
                    Modifier
                  </Button>
                </p>
              )}
              {setMail.error ? <ErrorBanner>{errorOf(setMail.error)}</ErrorBanner> : null}
              <p className="zy-texte-muet">C’est à cette adresse que vous êtes prévenu quand un participant réserve.</p>
              <div className="cal-actions">
                <Button variant="ghost" onClick={() => setEditing(true)}>Changer d’adresse</Button>
                <Button variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>Débrancher</Button>
              </div>
            </div>
          ) : (
            <form className="write-form" onSubmit={submit} noValidate>
              <ol className="cal-steps">
                <li>Ouvrez <strong>Google Agenda</strong> sur ordinateur, puis la roue dentée &gt; <strong>Paramètres</strong>.</li>
                <li>À gauche, sous « Paramètres de mes agendas », cliquez sur votre agenda.</li>
                <li>Copiez l’<strong>adresse secrète au format iCal</strong> (elle se termine par <code>.ics</code>) et collez-la ici.</li>
              </ol>
              <Field
                label="Adresse secrète au format iCal"
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (!emailTouched) setEmail(emailFromCalendarUrl(e.target.value));
                }}
                placeholder="https://calendar.google.com/calendar/ical/…/private-…/basic.ics"
                autoComplete="off"
                hint="Elle reste chiffrée et n’est jamais réaffichée. Zygosight n’y lit que les horaires occupés : ni les titres ni les lieux. Outlook et iCloud fonctionnent aussi."
              />
              <Field
                label="Votre e-mail"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailTouched(true); }}
                placeholder={cal?.accountEmail ?? 'vous@exemple.fr'}
                autoComplete="email"
                hint="Rempli d’après l’adresse de Google : c’est là que vous serez prévenu quand un participant réserve."
              />
              {save.error ? <ErrorBanner>{errorOf(save.error)}</ErrorBanner> : null}
              <div className="cal-actions">
                <Button type="submit" variant="accent" loading={save.isPending} disabled={!url.trim()}>
                  Brancher mon agenda
                </Button>
                {editing ? <Button variant="ghost" onClick={() => setEditing(false)}>Annuler</Button> : null}
              </div>
              {cal?.demo ? (
                <p className="zy-texte-muet">
                  Démonstration : n’importe quelle adresse Google Agenda est acceptée, et un agenda fictif est lu.
                </p>
              ) : null}
            </form>
          )}
        </>
      )}
    </section>
  );
}
